/* eslint-disable camelcase */
/* Fluxo alternativo ao certificado da empresa: emissão por procuração eletrônica
   (Guia do Emissor Público Nacional Web v1.3, tópico 11). A empresa outorga no e-CAC o
   serviço NFS-e a um CNPJ procurador e a nota sai com o certificado dele.
   O procurador sai de uma lista fechada: empresas que um Administrador (grupo 1) autorizou na tabela
   de certificados. Nunca uma empresa qualquer, porque o certificado escolhido assina a
   nota de outra empresa.
   O padrão continua sendo o certificado da própria empresa; aqui só se liga ou desliga
   a alternativa, empresa por empresa. */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  ListItem,
  OrderedList,
  Select,
  Text,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowCounterClockwise, Handshake } from 'phosphor-react';
import { useNfseEmissao } from '../../../../../hooks/useNfseEmissao';
import {
  INfseEmitente,
  INfseEmpresaBusca,
  INfseProcuracao,
  INfseProcuradorDaRede,
} from '../../../../../types/nfseEmissao';
import { extractApiErrorMessage } from '../../../../../utils/ErrorUtils';
import { EmpresaBusca } from '../shared/EmpresaBusca';
import { island } from '../shared/NfseIsland';
import { Banner, C, cnpjMask, dataBR, NButton } from '../shared/ui';

const hojeIso = () => new Date().toISOString().slice(0, 10);

const rotulo = (p: INfseProcuradorDaRede) => {
  const fim = p.situacao === 'pronto' ? '' : ' · indisponível';
  return `${p.razao_social} · ${cnpjMask(p.cnpj)}${fim}`;
};

export function ProcuracaoForm({
  emitentes,
  inicial,
}: {
  emitentes: INfseEmitente[];
  inicial: INfseEmpresaBusca | null;
}) {
  const queryClient = useQueryClient();
  const { getProcuradores, definirProcuracao, removerProcuracao } = useNfseEmissao();
  const [empresa, setEmpresa] = useState<INfseEmpresaBusca | null>(inicial);
  const [cnpjEscolhido, setCnpjEscolhido] = useState('');
  const [validaAte, setValidaAte] = useState('');

  const procuradoresQuery = useQuery({
    queryKey: ['nfse-procuradores'],
    queryFn: getProcuradores,
    staleTime: 60_000,
  });
  const procuradores = procuradoresQuery.data ?? [];
  const atual = useMemo(
    () => emitentes.find(e => e.empresa_id === empresa?.empresa_id),
    [emitentes, empresa],
  );
  const usaProcuracao = atual?.modo_certificado === 'procuracao';
  const escolhido = procuradores.find(p => p.cnpj === cnpjEscolhido);
  const pronto = escolhido?.situacao === 'pronto';
  const ehOProcurador = !!escolhido && escolhido.empresa_id === empresa?.empresa_id;

  useEffect(() => {
    if (inicial) setEmpresa(inicial);
  }, [inicial]);

  // Abre no procurador que a empresa já usa; senão no primeiro pronto da lista.
  useEffect(() => {
    if (!procuradores.length) return;
    const doEmitente = procuradores.find(p => p.empresa_id === atual?.procurador?.empresa_id);
    const primeiro = procuradores.find(p => p.situacao === 'pronto') ?? procuradores[0];
    setCnpjEscolhido((doEmitente ?? primeiro).cnpj);
    setValidaAte(atual?.procurador?.valida_ate ?? '');
  }, [procuradores, atual?.id, atual?.procurador?.empresa_id, atual?.procurador?.valida_ate]);

  const aposSalvar = (r: INfseProcuracao, texto: string) => {
    island.notify(texto, { tone: 'ok', meta: r.procurador?.razao_social ?? 'certificado próprio' });
    queryClient.invalidateQueries({ queryKey: ['nfse-emitentes'] });
    queryClient.invalidateQueries({ queryKey: ['nfse-painel'] });
    queryClient.invalidateQueries({ queryKey: ['nfse-ultima-nota'] });
  };

  const definir = useMutation({
    mutationFn: () =>
      definirProcuracao({
        empresa_id: (empresa as INfseEmpresaBusca).empresa_id,
        procurador_empresa_id: escolhido?.empresa_id ?? null,
        valida_ate: validaAte || null,
      }),
    onSuccess: r => aposSalvar(r, 'Procuração ligada'),
  });
  const remover = useMutation({
    mutationFn: () => removerProcuracao((empresa as INfseEmpresaBusca).empresa_id),
    onSuccess: r => aposSalvar(r, 'Voltou ao certificado da empresa'),
  });
  const erro = definir.error ?? remover.error;

  if (procuradoresQuery.isSuccess && !procuradores.length) {
    return (
      <Banner tone="warning" compact>
        Nenhuma empresa autorizada como procuradora. Um Administrador (grupo 1) precisa marcar o
        CNPJ procurador na tabela de certificados (botão &quot;Autorizar procurador&quot;).
      </Banner>
    );
  }

  return (
    <Flex direction="column" gap="14px">
      <Text fontSize="12.5px" color={C.ink2}>
        Alternativa ao certificado da empresa: a nota sai assinada com o certificado do CNPJ
        procurador, que recebeu procuração da empresa no e-CAC.
      </Text>
      <OrderedList fontSize="12px" color={C.subtle} spacing={1} pl={1}>
        <ListItem>
          A empresa entra em servicos.receitafederal.gov.br → Procurações → <b>Nova Autorização</b>.
        </ListItem>
        <ListItem>
          Informa o CNPJ procurador
          {escolhido?.cnpj ? ` (${cnpjMask(escolhido.cnpj)})` : ''} e a vigência.
        </ListItem>
        <ListItem>
          Marca o serviço <b>NFS-e</b> e assina a autorização.
        </ListItem>
        <ListItem>Aqui: escolha a empresa, o CNPJ procurador e a data de fim.</ListItem>
      </OrderedList>

      <FormControl isRequired>
        <FormLabel fontSize="12px" fontWeight={600} color={C.muted} mb={1}>
          Empresa que outorgou
        </FormLabel>
        <EmpresaBusca
          empresa={empresa}
          emitentes={emitentes}
          onSelecionar={e => {
            definir.reset();
            remover.reset();
            setEmpresa(e);
          }}
        />
      </FormControl>

      <FormControl isRequired>
        <FormLabel fontSize="12px" fontWeight={600} color={C.muted} mb={1} htmlFor="procurador">
          CNPJ procurador (emissor)
        </FormLabel>
        <Select
          id="procurador"
          value={cnpjEscolhido}
          onChange={e => {
            definir.reset();
            setCnpjEscolhido(e.target.value);
          }}
          isDisabled={procuradoresQuery.isLoading}
          borderRadius="10px"
          bg="rgba(255, 255, 255, 0.78)"
          borderColor={C.lineInput}
        >
          {procuradores.map(p => (
            <option key={p.empresa_id} value={p.cnpj}>
              {rotulo(p)}
            </option>
          ))}
        </Select>
        <FormHelperText fontSize="11.5px" color={C.subtle}>
          {escolhido?.certificado_valido_ate
            ? `Certificado até ${dataBR(escolhido.certificado_valido_ate)}. `
            : ''}
          Só aparecem as empresas autorizadas por um Administrador.
        </FormHelperText>
      </FormControl>
      {escolhido && !pronto && (
        <Banner tone="warning" compact>
          {escolhido.mensagem}
        </Banner>
      )}
      {ehOProcurador && (
        <Banner tone="info" compact>
          Esta empresa é o próprio procurador: ela emite com o certificado dela.
        </Banner>
      )}

      <FormControl>
        <FormLabel fontSize="12px" fontWeight={600} color={C.muted} mb={1} htmlFor="valida-ate">
          Procuração válida até
        </FormLabel>
        <Input
          id="valida-ate"
          type="date"
          min={hojeIso()}
          value={validaAte}
          onChange={e => setValidaAte(e.target.value)}
          borderRadius="10px"
          bg="rgba(255, 255, 255, 0.78)"
          borderColor={C.lineInput}
        />
        <FormHelperText fontSize="11.5px" color={C.subtle}>
          A mesma do e-CAC. Depois dela a tela bloqueia o envio; sem data, quem recusa é a Sefin.
        </FormHelperText>
      </FormControl>

      {usaProcuracao && atual?.procurador && (
        <Banner tone="success" compact>
          Já emite por procuração: <b>{atual.procurador.razao_social}</b>
          {atual.procurador.valida_ate ? ` até ${dataBR(atual.procurador.valida_ate)}` : ''}.
        </Banner>
      )}
      {erro && (
        <Banner tone="danger" compact>
          {extractApiErrorMessage(erro, 'Não foi possível salvar a procuração.')}
        </Banner>
      )}

      <NButton
        icon={Handshake}
        isDisabled={!empresa || !pronto || ehOProcurador || definir.isPending}
        onClick={() => definir.mutate()}
      >
        {usaProcuracao ? 'Atualizar procuração' : 'Emitir por procuração'}
      </NButton>
      {usaProcuracao && (
        <NButton
          variant="secondary"
          icon={ArrowCounterClockwise}
          isDisabled={remover.isPending}
          onClick={() => remover.mutate()}
        >
          Voltar ao certificado da empresa
        </NButton>
      )}
    </Flex>
  );
}
